import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, FileText, Trash2, File, Download, Upload, Loader2, Calendar as CalIcon, FileSearch } from 'lucide-react';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';

const typeColors = { lease: 'bg-blue-100 text-blue-700', receipt: 'bg-green-100 text-green-700', inspection: 'bg-purple-100 text-purple-700', contract: 'bg-orange-100 text-orange-700', report: 'bg-cyan-100 text-cyan-700', other: 'bg-gray-100 text-gray-700' };

export default function Documents() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({ name: '', document_type: 'contract', notes: '', expiry_date: '', file: null });

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try { const res = await api.getDocuments(); setDocuments(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.file) { setError(isAr ? 'يرجى اختيار ملف' : 'Please choose a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', formData.file);
      fd.append('document_type', formData.document_type);
      if (formData.name) fd.append('name', formData.name);
      if (formData.notes) fd.append('notes', formData.notes);
      if (formData.expiry_date) fd.append('expiry_date', formData.expiry_date);
      await api.uploadDocument(fd);
      setSuccess(isAr ? 'تم رفع المستند بنجاح' : 'Document uploaded successfully');
      setDialogOpen(false);
      setFormData({ name: '', document_type: 'contract', notes: '', expiry_date: '', file: null });
      fetchDocuments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e?.response?.data?.detail || (isAr ? 'فشل الرفع' : 'Upload failed'));
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isAr ? 'حذف المستند؟' : 'Delete document?')) {
      try { await api.deleteDocument(id); fetchDocuments(); } catch (e) { console.error(e); }
    }
  };

  const handleDownload = (doc) => {
    if (!doc.file_url) return;
    const a = document.createElement('a');
    a.href = doc.file_url;
    a.download = doc.file_name || doc.name || 'document';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const types = ['all', 'lease', 'receipt', 'inspection', 'contract', 'report', 'other'];
  const typeLabels = isAr ? { all: 'الكل', lease: 'عقد إيجار', receipt: 'إيصال', inspection: 'فحص', contract: 'عقد', report: 'تقرير', other: 'أخرى' } : { all: 'All', lease: 'Lease', receipt: 'Receipt', inspection: 'Inspection', contract: 'Contract', report: 'Report', other: 'Other' };

  let filtered = filterType === 'all' ? documents : documents.filter(d => d.document_type === filterType);
  if (search) filtered = filtered.filter(d => (d.name || '').toLowerCase().includes(search.toLowerCase()) || (d.notes || '').toLowerCase().includes(search.toLowerCase()));

  const isExpiringSoon = (exp) => {
    if (!exp) return false;
    const days = (new Date(exp) - new Date()) / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 30;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><FileText className="h-5 w-5 text-blue-600" strokeWidth={1.5} /></div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'إدارة المستندات' : 'Document Management'}</h1>
              <p className="text-sm text-gray-500">{isAr ? 'تنظيم وإدارة جميع المستندات العقارية' : 'Organize and manage all property documents'}</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gradient-primary text-white rounded-xl" data-testid="docs-upload-btn"><Plus className="h-4 w-4 me-2" /> {isAr ? 'رفع مستند' : 'Upload Document'}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{isAr ? 'رفع مستند جديد' : 'Upload New Document'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'الملف' : 'File'}*</Label>
                  <label className="mt-1 block border-2 border-dashed border-gray-200 rounded-xl p-5 text-center hover:border-primary/40 transition-colors cursor-pointer" data-testid="docs-file-drop">
                    <input type="file" className="hidden" onChange={e => setFormData({ ...formData, file: e.target.files[0], name: formData.name || (e.target.files[0]?.name || '') })} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.xlsx,.csv" data-testid="docs-file-input" />
                    <Upload className="h-7 w-7 text-gray-400 mx-auto mb-2" />
                    {formData.file ? (
                      <div>
                        <p className="text-sm font-medium text-primary">{formData.file.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{(formData.file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">{isAr ? 'اختر ملفاً (حد أقصى 10MB)' : 'Choose a file (max 10MB)'}</p>
                    )}
                  </label>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'اسم المستند' : 'Document Name'}</Label>
                  <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder={isAr ? 'سيتم استخدام اسم الملف إذا تُرك فارغاً' : 'File name will be used if left blank'} data-testid="docs-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'النوع' : 'Type'}*</Label>
                  <Select value={formData.document_type} onValueChange={v => setFormData({ ...formData, document_type: v })}>
                    <SelectTrigger data-testid="docs-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{types.filter(t => t !== 'all').map(t => <SelectItem key={t} value={t}>{typeLabels[t]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'تاريخ الانتهاء (اختياري)' : 'Expiry Date (optional)'}</Label>
                  <Input type="date" value={formData.expiry_date} onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} data-testid="docs-expiry-input" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">{isAr ? 'ملاحظات' : 'Notes'}</Label>
                  <Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} data-testid="docs-notes-input" />
                </div>
                {error && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>}
                <Button type="submit" disabled={uploading} className="w-full gradient-primary text-white rounded-xl" data-testid="docs-submit">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isAr ? 'رفع' : 'Upload')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {success && <div className="text-sm text-green-700 bg-green-50 border border-green-100 px-4 py-2.5 rounded-xl" data-testid="docs-success-banner">{success}</div>}

        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {types.filter(t => t !== 'all').map(t => (
            <button key={t} onClick={() => setFilterType(t)} className={`text-start ${filterType === t ? 'ring-2 ring-primary' : ''} rounded-2xl`}>
              <Card className="shadow-soft border-gray-100 hover:border-primary/30 transition-colors">
                <CardContent className="pt-4 pb-3 text-center">
                  <div className="text-xl font-heading font-bold">{documents.filter(d => d.document_type === t).length}</div>
                  <div className="text-xs text-gray-500">{typeLabels[t]}</div>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-2 overflow-x-auto pb-1 flex-1 min-w-0">
            {types.map(t => <button key={t} onClick={() => setFilterType(t)} className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${filterType === t ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-600'}`} data-testid={`docs-filter-${t}`}>{typeLabels[t]}</button>)}
          </div>
          <div className="relative">
            <FileSearch className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={isAr ? 'بحث...' : 'Search...'} className="ps-9 w-56 rounded-xl" data-testid="docs-search" />
          </div>
        </div>

        <Card className="shadow-soft border-gray-100">
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 flex items-center justify-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <File className="h-8 w-8 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold text-gray-900 mb-1">{isAr ? 'لا توجد مستندات بعد' : 'No documents yet'}</h3>
                <p className="text-sm text-gray-500 mb-4">{isAr ? 'ابدأ برفع أول مستند: عقود، إيصالات، تقارير فحص' : 'Get started by uploading your first document: contracts, receipts, inspection reports.'}</p>
                <Button onClick={() => setDialogOpen(true)} className="gradient-primary text-white rounded-xl" data-testid="docs-empty-cta">
                  <Plus className="h-4 w-4 me-1.5" /> {isAr ? 'ارفع أول مستند' : 'Upload first document'}
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>{isAr ? 'المستند' : 'Document'}</TableHead>
                  <TableHead>{isAr ? 'النوع' : 'Type'}</TableHead>
                  <TableHead>{isAr ? 'انتهاء الصلاحية' : 'Expires'}</TableHead>
                  <TableHead>{isAr ? 'ملاحظات' : 'Notes'}</TableHead>
                  <TableHead>{isAr ? 'تم الرفع' : 'Uploaded'}</TableHead>
                  <TableHead>{isAr ? 'إجراءات' : 'Actions'}</TableHead>
                </TableRow></TableHeader>
                <TableBody>{filtered.map(doc => (
                  <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                    <TableCell className="font-medium">
                      <div>{doc.name}</div>
                      {doc.file_size && <div className="text-xs text-gray-400">{(doc.file_size / 1024).toFixed(1)} KB</div>}
                    </TableCell>
                    <TableCell><span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[doc.document_type] || typeColors.other}`}>{typeLabels[doc.document_type]}</span></TableCell>
                    <TableCell>
                      {doc.expiry_date ? (
                        <span className={`text-xs flex items-center gap-1 ${isExpiringSoon(doc.expiry_date) ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                          <CalIcon className="h-3 w-3" /> {new Date(doc.expiry_date).toLocaleDateString()}
                          {isExpiringSoon(doc.expiry_date) && <span className="ms-1 px-1.5 py-0.5 bg-red-50 rounded text-[10px]">{isAr ? 'قريباً' : 'soon'}</span>}
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500 max-w-xs truncate">{doc.notes || '-'}</TableCell>
                    <TableCell className="text-xs text-gray-500">{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                    <TableCell><div className="flex gap-1">
                      {doc.file_url && <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`doc-download-${doc.id}`}><Download className="h-4 w-4" /></Button>}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} data-testid={`doc-delete-${doc.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <ModuleIntelligenceBlock module="documents" testId="documents-intel" />
      </div>
    </Layout>
  );
}
